import { AuthenticationProvider, OidcSecure } from '@axa-fr/react-oidc-context';
import { AppProvider } from 'context/AppProvider';
import { SocketProvider } from 'context/SocketProvider';
import PrimeReact from 'primereact/api';
import PageProgress from 'react-page-progress';
import { Provider } from 'react-redux';
import store from 'app/store';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from 'components/ErrorBoundary/ErrorFallback';
import { QueryClient, QueryClientProvider, useQueryErrorResetBoundary } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import EmptyPage from 'components/EmptyPage/EmptyPage';
import { oidcConfiguration } from './oidc';

import 'primereact/resources/themes/bootstrap4-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'assets/style/layout.scss';
import 'sweetalert2/src/sweetalert2.scss';

const AUTH_PATHS = ['/authentication/callback', '/authentication/silent_callback'];
const RETURN_LOCATION_KEY = 'econ.auth.return_location';

const WrapApp = () => {
    const LazyApp = lazy(() => import('./App'));
    const [queryClient] = useState(() => new QueryClient());

    useEffect(() => {
        queryClient.setDefaultOptions({
            queries: {
                staleTime: 10000,
                keepPreviousData: true,
                refetchOnWindowFocus: false,
            },
        });

        if (!AUTH_PATHS.includes(window.location.pathname)) {
            sessionStorage.setItem(
                RETURN_LOCATION_KEY,
                window.location.pathname + window.location.search + window.location.hash
            );
        }
    }, [queryClient]);

    const { reset } = useQueryErrorResetBoundary();
    PrimeReact.ripple = true;
    PrimeReact.autoZIndex = true;

    return (
        <div className="WrapApp">
            <ErrorBoundary onReset={reset} FallbackComponent={ErrorFallback}>
                <SocketProvider>
                    <Provider store={store}>
                        <AppProvider>
                            <QueryClientProvider client={queryClient}>
                                <AuthenticationProvider
                                    configuration={oidcConfiguration}
                                    authenticating={EmptyPage}
                                    callbackComponentOverride={EmptyPage}
                                >
                                    <PageProgress height={4} className="PageProgress" />
                                    <Suspense fallback={<EmptyPage />}>
                                        <OidcSecure>
                                            <LazyApp />
                                        </OidcSecure>
                                    </Suspense>
                                    <ReactQueryDevtools initialIsOpen />
                                </AuthenticationProvider>
                            </QueryClientProvider>
                        </AppProvider>
                    </Provider>
                </SocketProvider>
            </ErrorBoundary>
        </div>
    );
};

export default WrapApp;